module Meevo.Plugins.Register {
    export enum RegisterSectionTypeEnum {
        Services = 0,
        GiftCard = 1,
        Products = 2,
        ClientAccounts = 3,
        Package = 4,
        Drawer = 5,
        Client = 6,
        Cash = 7,
        Employee = 8,
        Check = 9,
        Buttons = 10,
        Ticket = 11,
        CreditMultiCard = 12,
        Other = 13,
        Memberships = 14,
        Discounts = 15,
        AutomaticDiscounts = 16,
        Payment = 17,
        GiftCardPayment = 18,
        Tips = 19,
        ClientPoints = 20,
        PackageRedemption = 21,
        Empty = 22,
        PackageTransfer = 23,
        SplitCommission = 24,
        RefundReason = 25,
        ClientAccountPayment = 26,
        PaymentOptions = 27,
        RefundOptions = 28,
        ChangeOptions = 29,
        CardOnFile = 30,
        Teacher = 31, // Only used for teacher selection when importing appointments
        CreditSingleCard = 32,
        Self = 33,
        CareCreditPayment = 34,
        PaymentPlan = 35,
        PaymentPlanRefund = 36,
        ProductUsage = 37,
    }

    export enum RegisterSectionCatTypeEnum {
        Sale = 0,
        Payment = 1,
        Other = 2
    }
    export enum RefundTypes {
        History = 0,
        Manual = 1
    }

    export interface PassiveAlert {
        alertPriorityLevel: Meevo.Model.AlertPriorityLevelEnum;
        alertMessage: string;
    }

    export interface RegisterPaymentCat {
        sectionType?: RegisterSectionTypeEnum;
        id: Model.PaymentCategoryEnum;
        originalDisplayName?: string;
        displayName: string;
        securityKey: string;
        paymentCategory: Model.PaymentCategoryEnum;
        isRefundable?: boolean;
        sequence?: number;
        iconName?: string;
        isHidden?: boolean;
        isDisabled?: boolean;
        disabledMessage?: string;
    }

    export class DrawerManager implements Meevo.Model.DrawerManager {
        public displayName: string;
        public id: string;
        constructor() {
            this.displayName = '';
            this.id = '';
        }
    }

    export class SalePayment implements Model.SalePayment {
        amount: number;
        paymentTypeId: string;
        isPending: boolean;
        tempId: string;
        paymentCategory: Model.PaymentCategoryEnum;
        __type: string;

        constructor(__type: string) {
            this.__type = __type;
        }
    }

    export class PaymentPlanSalePayment extends SalePayment {
        public paymentPlanId: string;
        constructor(salePayment: Model.PaymentPlanPayment) {
            super("Millennium.Meevo.Register.Contracts.Model.SalePaymentPaymentPlan, Millennium.Meevo.Register.Contracts.Model")
            this.amount = salePayment.amount;
            this.paymentTypeId = salePayment.paymentTypeId;
            this.paymentCategory = salePayment.paymentCategory;
        }
    }

    export class RegisterClient implements Meevo.Model.IRegisterClient {
        public client: Model.SaleClient;
        public clientType: Model.ClientTypeEnum;
        public clientDisplayName?: string;
        public appointmentId: string;
        public appointmentIds: string[];
        public clientIdCheckedForMembership: string;
        public expandAlerts: boolean;

        constructor(rclient?: Meevo.Model.IRegisterClient) {
            if (rclient) {
                this.client = rclient.client;
                this.clientType = rclient.clientType;
                this.clientDisplayName = rclient.clientDisplayName;
                this.appointmentId = rclient.appointmentId;
                this.clientIdCheckedForMembership = (rclient.client) ? rclient.client.clientIdCheckedForMembership : null;
            }

            this.appointmentIds = [];

            if (this.appointmentId) {
                this.appointmentIds.push(this.appointmentId);
            }
        }

        getDisplayName = () => {
            if (this.clientDisplayName) {
                return this.clientDisplayName;
            }

            return this.client
                ? this.client.displayName
                : this.clientType
                    ? Model.ClientTypeEnum[this.clientType].replace("Anonymous", "Anonymous ")
                    : "";
        }

        getId = () => {
            return (this.clientType === Model.ClientTypeEnum.Client) ? this.client.id : "";
        }

        addRelatedAppointment = (appointmentId) => {
            if (this.clientType === Meevo.Model.ClientTypeEnum.Client && appointmentId && this.appointmentIds.indexOf(appointmentId) < 0) {
                this.appointmentIds.push(appointmentId);
            }
        }

        isMatch = (rclient: Meevo.Model.IRegisterClient): boolean => {
            return ((this.client && rclient.client && this.client.id == rclient.client.id) ||
                (this.clientType && rclient.clientType && ((this.appointmentIds.length == 0 && !rclient.appointmentId) || (this.appointmentIds.indexOf(rclient.appointmentId) >= 0))
                    && this.clientType !== Meevo.Model.ClientTypeEnum.Client && this.clientType == rclient.clientType));
        }
    }

    // NOTE: These classes are needed to ensure the saleLine.item object can be deserialized properly to its corresponding POCO type on the server-side.
    // The __type  property needs to be the first property (set) when the JS object is serialized to string then sent to the server. Setting the __type
    // property in the constructor ensures it is the first property in the object.
    export abstract class BaseSaleLineItem implements Model.SaleLineItem {
        public __type: string;
        public id: string;
        public displayName: string;
        public shortcut: string;
        public originalId: string;
        public entityVersion: number;
        public paymentPlanEntityVersion: number;

        constructor(item: Model.SaleLineItem, __type: string) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            this.__type = __type;
            this.id = item.id;
            this.originalId = item.originalId;
            this.displayName = item.displayName;
            this.shortcut = item.shortcut;
            this.paymentPlanEntityVersion = item.paymentPlanEntityVersion;
        }
    }

    export class PackageRedeemableSaleLineItem extends BaseSaleLineItem {
        public usedClientPackageId: string;
        public usedClientPackageServiceId: string;
        public usedClientPackageProductId: string;
        public refundClientPackageId: any;
        public isRefundClientPackage: boolean;
    }

    export class ServiceSaleLineItem extends PackageRedeemableSaleLineItem {
        public appointmentId: string;
        public appointmentServiceId: string;
        public appointmentServiceDate: string;
        public temp: Model.ServiceSaleLineItem;
        public addOnAppointmentServiceId: string;
        public addOnServiceId: string;
        public cancellationTypeId: string;
        public appointmentEntityVersion: number;
        public hasCheckoutCustomControls: boolean;
        public customControlsAnswers: any;
        public doNotAllowTips: boolean;
        public docsAndFormsShown: boolean;
        public isRebooked: boolean;
        public isPromoGiftCardUsed: boolean;
        public isUnlimitedService: boolean;
        public depositAmount: number;
        public isAddOn: boolean;
        public employeeId: string;
        public clientId: string;
        public clientType: Model.ClientTypeEnum;
        public appointmentServiceSaleDetails: Model.AppointmentServiceSaleDetails;

        constructor(item: Model.ServiceSaleLineItem) {
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.ServiceSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.appointmentId = item.appointmentId;
            this.appointmentServiceId = item.appointmentServiceId;
            this.appointmentServiceDate = item.appointmentServiceDate;
            this.addOnAppointmentServiceId = item.addOnAppointmentServiceId;
            this.addOnServiceId = item.addOnServiceId;
            this.cancellationTypeId = item.cancellationTypeId;
            this.appointmentEntityVersion = item.appointmentEntityVersion;
            this.usedClientPackageServiceId = item.usedClientPackageServiceId;
            this.usedClientPackageId = item.usedClientPackageId;
            this.refundClientPackageId = item.refundClientPackageId;
            this.isRefundClientPackage = item.isRefundClientPackage;
            this.hasCheckoutCustomControls = item.hasCheckoutCustomControls;
            this.customControlsAnswers = item.customControlsAnswers;
            this.doNotAllowTips = item.doNotAllowTips;
            this.isRebooked = item.isRebooked;
            this.isUnlimitedService = item.isUnlimitedService;
            this.depositAmount = item.depositAmount;
            this.employeeId = item.employeeId;
            this.clientId = item.clientId;
            this.clientType = item.clientType;
            this.temp = _.clone(item);
            this.isAddOn = item.isAddOn;
            this.appointmentServiceSaleDetails = item.appointmentServiceSaleDetails;
        }
    }

	export class AssociatedClientSaleLineItem extends BaseSaleLineItem {
		constructor(item: Model.SaleLineItem) {
			// __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
			let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.AssociatedClientSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
			super(item, __type);
		}
	}

    export class ProductSaleLineItem extends PackageRedeemableSaleLineItem {
        public productId: string;
        public sku: string;
        public barCode: string;
        public variantDisplayName: string;
        public variantAttributes: any[];
        public isResell: boolean;
        public isProductBundle: boolean;
        public productInventoryType: Model.ProductInventoryTypeEnum;

        constructor(item: Model.ProductSaleLineItem, type=undefined) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.ProductSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, type || __type);
            this.productId = item.productId;
            this.sku = item.sku;
            this.barCode = item.barCode;
            this.variantDisplayName = item.variantDisplayName;
            this.variantAttributes = [];
            this.isProductBundle = item.isProductBundle;
            _.each(item.variantAttributes, (elem) => { this.variantAttributes.push(_.clone(elem)); });
            this.isResell = item.isResell;
            this.productInventoryType = item.productInventoryType;
        }
    }

    export class CustomProductBundleSaleLineItem extends ProductSaleLineItem {
        public productBundle: any;

        constructor(item:any) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.CustomProductBundleSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.productBundle = item.productBundle;
        }
    }

    export class MembershipSaleLineItem extends BaseSaleLineItem {
        public paymentAccounts: any[];
        public recurringFee: number;
        public enrollmentFee: number;
        public startDate: Date;
        public expiresOnDate: Date;
        public firstPaymentDate: Date;
        public isAutoRenewed: boolean;
        public renewalFee: number;
        public clientId: string;
        public membershipId: Guid;
        public membershipNumber: string;
        public client: Model.ClientListItem;
        public autoRenewType: Model.AutoRenewTypeEnum;
        public isBillable: boolean;

        public isTaxExempt: boolean;
        public minEnrollmentFee: number;
        public maxEnrollmentFee: number;
        public clientPackages: any[];

        constructor(item: Model.MembershipSaleLineItem) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.MembershipSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.paymentAccounts = item.paymentAccounts;
            this.autoRenewType = item.autoRenewType;
            this.clientPackages = item.clientPackages;
        }
    }

    export class MembershipUpgradeSaleLineItem extends MembershipSaleLineItem {
        public originalMembershipCancellationReason: Meevo.Model.MembershipCancellation;
        public originalMembershipId: string;
        public originalMembershipNumber: string;
        public originalMembershipDisplayName:string;
        public originalMembershipCancellationDate: Date;
        public originalMembershipEntityVersion: number;
        public accruedServiceExpirationDate?: Date;
        public newMembershipDisplayName: string;

        constructor(item: Model.MembershipSaleLineItem) {
            super(item);
            this.__type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.MembershipUpgradeSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
        }
    }

    export class ClientPackageTransferSaleLineItem extends BaseSaleLineItem {
        public clientPackage: any;
        public donorClientPackageServiceId: string;
        public donorClientPackageProductId: string;
        public isPackageTransferOverride: boolean;
        public donorClientId: string;

        constructor(item: Model.SaleLineItem) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.ClientPackageTransferSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
        }
    }

    export class MembershipPaymentSaleLineItem extends BaseSaleLineItem {
        public clientMembershipBillFeeType: number;
        public clientMembershipBillStatus: number;
        public clientMembershipId: string;
        public entityVersion: number;
        public freezeSetting: boolean;
        public freezeStartDate: string;
        public freezeReactivationDate: string;
        public freezeReason: string;
        public isTaxExempt: boolean;
        public billPackages?: Array<Guid>;

        constructor(item: Model.MembershipPaymentSaleLineItem) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.MembershipPaymentSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.clientMembershipBillFeeType = item.clientMembershipBillFeeType;
            this.clientMembershipBillStatus = item.clientMembershipBillStatus;
            this.clientMembershipId = item.clientMembershipId;
            this.entityVersion = item.entityVersion;
            this.freezeSetting = item.freezeSetting;
            this.freezeStartDate = item.freezeStartDate;
            this.freezeReactivationDate = item.freezeReactivationDate;
            this.freezeReason = item.freezeReason;
            this.isTaxExempt = item.isTaxExempt;
            this.billPackages = item.billPackages
        }
    }

    export class PaymentPlanPaymentSaleLineItem extends BaseSaleLineItem {
        paymentDate: Date;
        saleTransactionId: string;
        constructor(item: Model.PaymentPlanPaymentSaleLineItem) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.PaymentPlanPaymentSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.paymentDate = item.paymentDate;
            this.saleTransactionId = item.saleTransactionId;
        }
    }

    export class PaymentPlanReductionSaleLineItem extends BaseSaleLineItem {
        constructor(item: Model.PaymentPlanReductionSaleLineItem) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.PaymentPlanReductionSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
        }
    }

    export class PackageSaleLineItem extends BaseSaleLineItem {
        public clientPackage: any;
        public displayName: string;
        public unlimitedTotal: number;
        constructor(item: Model.PackageSaleLineItem) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.PackageSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.clientPackage = item.clientPackage;
            this.unlimitedTotal = item.unlimitedTotal;
        }
    }

    export class GiftCardSaleLineItem extends BaseSaleLineItem {
        public clientGiftCard: any;
        public remainingAmount: number;
        public remainingRefundAmount: number;
        public giftCardNumber: string;
        public giftCardNumberUnmasked: string;
        public autoGenerateGiftCardNumber: boolean;
        public note: string;
        public isGiftCardNumberUnmasked: boolean;

        constructor(item: Model.GiftCardSaleLineItem, type?: string) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = type || "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.GiftCardSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.clientGiftCard = item.clientGiftCard;
            this.remainingAmount = item.remainingAmount;
            this.remainingRefundAmount = item.remainingRefundAmount;
            this.giftCardNumber = item.giftCardNumber;
            this.autoGenerateGiftCardNumber = item.autoGenerateGiftCardNumber;
            this.note = item.note;
            this.isGiftCardNumberUnmasked = item.isGiftCardNumberUnmasked;
        }
    }

    export class GiftCardBulkSaleLineItem extends GiftCardSaleLineItem {
        giftCardNumberStarting?: string;
        giftCardNumberEnding?: string;

        constructor(item: Model.GiftCardBulkSaleLineItem) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.GiftCardBulkSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.giftCardNumberStarting = item.giftCardNumberStarting;
            this.giftCardNumberEnding = item.giftCardNumberEnding;
        }
    }

    export class ClientAccountSaleLineItem extends BaseSaleLineItem {
        public client: any;
        public clientId: any;
        public originalBalance: number;
        public appointmentServiceId: string;
        public clientDisplayName: string;

        constructor (item) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.ClientAccountSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.client = item.client;
            this.clientId = item.clientId;
            this.originalBalance = item.originalBalance;
            this.appointmentServiceId = item.appointmentServiceId;
        }
    }

    export class PackageServiceSwapFeeSaleLineItem extends BaseSaleLineItem {
        public clientPackageServiceId: string;
        public paymentPromoGiftCardId: string;
        public swapGroupId: string;
        constructor(item: Model.PackageServiceSwapFeeSaleLineItem) {
            // __type always needs to be set first so that the deserializer will be able to deserialize this object to the correct POCO
            let __type = "Millennium.Meevo.Register.Contracts.Model.SaleLineItems.PackageServiceSwapFeeSaleLineItem, Millennium.Meevo.Register.Contracts.Model";
            super(item, __type);
            this.clientPackageServiceId = item.clientPackageServiceId;
            this.paymentPromoGiftCardId = item.paymentPromoGiftCardId;
            this.swapGroupId = item.swapGroupId;
        }
    }

    export interface SaleLineTransactionSelection {
        selectAllItems: boolean;    // flag to select all items by default
        allItemsSelected: boolean;  // flag for "All Items" checkbox, if present
        selections: any[];
        selectionTotal: number;
    }

    export class RegisterTipLine implements Model.TipLine {
        employeeId: string;
        employee: Model.EmployeeListItem;
        originalEmployeeId: string;
        originalEmployee: Model.EmployeeListItem;
        amount: number;
        baseTotal: number;
        finalTotal: number;
        serviceTotal: number;
        tippableServiceTotal: number;
        percentage: number;
        forcedDisplayValue: number;
        isServiceEmployee: boolean;
        prepaidTipMinimum: number;
        externalTipTransactionId?: string;
        isRefund?: boolean;
    }

    export interface RefundPackageRedemptionPackageItem {
        refundSaleId: number;
        refundSaleLineId: Number,
        refundLocationId: number,
        refundTransactionId: string,
        packageItems: Model.IClientPackageItem[]
    }
}